// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { useAppsBackend, useElementDimensions } from "@mysten/core";
import { LoadingIndicator } from "@mysten/ui";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useRef, type ReactNode } from "react";

import { useNetworkContext } from "~/context";
import { Banner } from "~/ui/Banner";
import { Network } from "~/utils/api/DefaultRpcClient";
import Header from "../header/Header";
import Footer from "../footer/Footer";

export type PageLayoutProps = {
  gradient?: {
    content: ReactNode;
    size: "lg" | "md";
  };
  isError?: boolean;
  content: ReactNode;
  loading?: boolean;
};

const DEFAULT_HEADER_HEIGHT = 64;

/** 与左侧固定 aside（w-64）对齐 */
const SIDEBAR_OFFSET = "md:pl-64";

/** H5 收紧左右 gutter；md+ 逐步放宽，避免与页面内层 px 叠加过宽 */
const PAGE_GUTTER =
  "mx-auto w-full max-w-[1440px] px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10";

/** 首页 / DEX / 跨链 / 质押 / 应用：header 下方到首屏内容的统一上间距 */
export const PAGE_CONTENT_TOP = "pt-4 md:pt-6";

export function PageLayout({
  gradient,
  content,
  loading,
  isError,
}: PageLayoutProps) {
  const [network] = useNetworkContext();
  const { request } = useAppsBackend();
  const outageOverride = false;

  const { data } = useQuery({
    queryKey: ["apps-backend", "monitor-network"],
    queryFn: () =>
      request<{ degraded: boolean }>("monitor-network", {
        project: "EXPLORER",
      }),
    // Keep cached for 2 minutes:
    staleTime: 2 * 60 * 1000,
    retry: false,
    enabled: network === Network.MAINNET,
  });
  const isGradientVisible = !!gradient;
  const renderNetworkDegradeBanner =
    outageOverride || (network === Network.MAINNET && data?.degraded);
  const headerRef = useRef<HTMLElement | null>(null);
  const [headerHeight] = useElementDimensions(headerRef, DEFAULT_HEADER_HEIGHT);

  const networkDegradeBannerCopy =
    network === Network.TESTNET
      ? "Sui Explorer (Testnet) is currently under-going maintenance. Some data may be incorrect or missing."
      : "The explorer is running slower than usual. We're working to fix the issue and appreciate your patience.";

  return (
    <div className="relative min-h-screen w-full bg-[#1E293B]">
      <section
        ref={headerRef}
        className="fixed top-0 z-20 flex w-full flex-col"
      >
        {renderNetworkDegradeBanner && (
          <Banner rounded="none" align="center" variant="warning" fullWidth>
            <div className="break-normal">{networkDegradeBannerCopy}</div>
          </Banner>
        )}
      <div className="md:pl-64 md:pt-16">
        <Header />
      </div>
      </section>
      {loading && (
        <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 transform justify-center md:left-[calc(50%+8rem)]">
          <LoadingIndicator variant="lg" />
        </div>
      )}
      <main
        className={clsx("relative z-10 min-w-0 bg-[#1E293B]", SIDEBAR_OFFSET)}
        style={
          !isGradientVisible
            ? {
                paddingTop: `${headerHeight}px`,
              }
            : undefined
        }
      >
        {isGradientVisible ? (
          <section
            style={{
              paddingTop: `${headerHeight}px`,
            }}
            className={clsx(
              "group/gradientContent",
              loading && "bg-[#1E293B]",
              isError && "bg-gradients-failure",
              !isError && "bg-[#1E293B]",
            )}
          >
            <div
              className={clsx(
                PAGE_GUTTER,
                PAGE_CONTENT_TOP,
                gradient.size === "lg" && "pb-4 md:pb-8 xl:pb-12",
                gradient.size === "md" && "pb-1 md:pb-2",
              )}
            >
              {gradient.content}
            </div>
          </section>
        ) : null}
        {!loading && (
          <section
            className={clsx(
              PAGE_GUTTER,
              "pb-16 md:pb-20",
              isGradientVisible ? "pt-0" : PAGE_CONTENT_TOP,
            )}
          >
            {content}
          </section>
        )}
      </main>
      <div className={SIDEBAR_OFFSET}>
        <Footer />
      </div>
    </div>
  );
}
